/**
 * SCHEMA del libro "REDMIL Power Builders · Sistema de Control del Taller Eléctrico".
 * Describe el estado DESPUÉS de la migración (OT-01 R:T automáticas).
 * Los encabezados se usan para detectar deriva de estructura: si la hoja no coincide,
 * la escritura en esa hoja se bloquea.
 * Los VALORES de las listas NO están aquí: se leen de la hoja "Listas" en tiempo de ejecución.
 */
import type { Role } from './roles';

export const HEADER_ROW = 4;
export const FIRST_DATA_ROW = 5;
export const LAST_DATA_ROW = 204;
export const MAX_RECORDS = LAST_DATA_ROW - FIRST_DATA_ROW + 1;

export const FORMAT_SHEETS = [
  'OT-01', 'ING-01', 'MAT-01', 'MAT-02', 'INV-01', 'PRD-01', 'CAL-01',
  'CAL-02', 'FAT-01', 'SEG-01', 'INS-01', 'NCR-01', 'DES-01', 'COS-01',
] as const;
export type SheetName = (typeof FORMAT_SHEETS)[number];

export const LISTS_SHEET = 'Listas';
export const DASHBOARD_SHEET = 'Dashboard';
export const LOG_SHEET = 'Bitácora';

/** Columnas de la hoja Listas (fila 1 = nombre técnico). */
export const LIST_NAMES = [
  'tipo_tab', 'etapa', 'estado_ot', 'sino', 'sinona', 'tipo_doc', 'est_apr', 'tipo_mov', 'unidad', 'fase',
  'punto', 'res_insp', 'u_torque', 'prueba', 'res_fat', 'tipo_hall', 'est_seg', 'tipo_ncr', 'est_ncr', 'cond_inst',
] as const;
export type ListName = (typeof LIST_NAMES)[number];

export type FieldType =
  | 'code' // texto llave: MAYÚSCULAS, sin comodines * ? ~ ni operadores iniciales
  | 'text'
  | 'longText'
  | 'date' // serial de Sheets
  | 'number'
  | 'int'
  | 'money'
  | 'percent' // se captura 0–100, se guarda 0–1
  | 'list'
  | 'ref';

export interface Condition {
  col: string;
  equals?: string;
  notEquals?: string;
  notBlank?: true;
}

export interface ColumnDef {
  col: string;
  id: string;
  header: string;
  kind: 'capture' | 'auto';
  type: FieldType;
  list?: ListName;
  ref?: 'OT-01' | 'INS-01';
  required?: boolean;
  requiredWhen?: Condition;
  min?: number;
  minExclusive?: boolean;
  max?: number;
  /** Fecha que debe ser >= a la de otra columna de la misma fila. */
  gteCol?: string;
  unique?: boolean;
  immutable?: boolean;
  /** Dato monetario: solo Administrador y Gerencia (decisión 3 del dueño). */
  cost?: boolean;
  maxLength?: number;
  /** Tipo antes de la migración (solo OT-01 R:T). */
  beforeMigration?: { kind: 'capture'; list: ListName };
}

export interface SheetDef {
  name: SheetName;
  id: string;
  title: string;
  lastCol: string;
  /** Columna llave única (o null si el registro es la fila). */
  keyCol: string | null;
  /** Columna con el código de tablero para el join con OT-01 (null = global). */
  boardCodeCol: string | null;
  /** COS-01: la fila es la misma que la del tablero en OT-01 (solo Editar). */
  rowLinkedToWorkOrders?: boolean;
  /** Hoja completa restringida a quien puede ver costos. */
  costSheet?: boolean;
  writeRoles: readonly Role[];
  exampleRows: readonly number[];
  columns: readonly ColumnDef[];
}

type Opts = Omit<ColumnDef, 'col' | 'id' | 'header' | 'kind' | 'type'>;
const cap = (col: string, id: string, header: string, type: FieldType, o: Opts = {}): ColumnDef => ({ col, id, header, kind: 'capture', type, ...o });
const auto = (col: string, id: string, header: string, type: FieldType, o: Opts = {}): ColumnDef => ({ col, id, header, kind: 'auto', type, ...o });
const R = true;

const BOARD = (col: string, header = 'Código de tablero', o: Opts = {}) => cap(col, 'boardCode', header, 'ref', { ref: 'OT-01', required: R, ...o });
const NOTES = (col: string) => cap(col, 'notes', 'Observaciones', 'longText');

export const SCHEMA: Readonly<Record<SheetName, SheetDef>> = {
  'OT-01': {
    name: 'OT-01', id: 'workOrders', title: 'Orden de trabajo por tablero', lastCol: 'U',
    keyCol: 'A', boardCodeCol: 'A', writeRoles: ['admin', 'shopManager'], exampleRows: [5],
    columns: [
      cap('A', 'boardCode', 'Código de tablero (único)', 'code', { required: R, unique: R, immutable: R }),
      cap('B', 'client', 'Cliente', 'text', { required: R }),
      cap('C', 'projectNo', 'N° de proyecto', 'text'),
      cap('D', 'boardType', 'Tipo de tablero', 'list', { list: 'tipo_tab', required: R }),
      cap('E', 'scope', 'Alcance', 'longText'),
      cap('F', 'owner', 'Responsable', 'text', { required: R }),
      cap('G', 'startDate', 'Fecha de inicio', 'date', { required: R }),
      cap('H', 'dueDate', 'Fecha comprometida', 'date', { required: R, gteCol: 'G' }),
      cap('I', 'deliveredDate', 'Fecha de entrega real', 'date', { gteCol: 'G' }),
      cap('J', 'stage', 'Etapa actual', 'list', { list: 'etapa', required: R }),
      cap('K', 'status', 'Estado', 'list', { list: 'estado_ot', required: R }),
      cap('L', 'budgetHours', 'Horas presupuestadas', 'number', { min: 0 }),
      cap('M', 'progress', 'Avance %', 'percent'),
      auto('N', 'actualHours', 'Horas reales (de PRD-01)', 'number'),
      auto('O', 'earnedHours', 'Horas estándar ganadas', 'number'),
      auto('P', 'daysLate', 'Días de atraso', 'int'),
      auto('Q', 'onTime', 'Entrega a tiempo', 'text'),
      auto('R', 'fatTested', 'Probado FAT', 'text', { beforeMigration: { kind: 'capture', list: 'sino' } }),
      auto('S', 'firstPassApproved', 'Aprobado a la primera', 'text', { beforeMigration: { kind: 'capture', list: 'sino' } }),
      auto('T', 'dossierComplete', 'Expediente completo', 'text', { beforeMigration: { kind: 'capture', list: 'sino' } }),
      NOTES('U'),
    ],
  },
  'ING-01': {
    name: 'ING-01', id: 'drawings', title: 'Control de planos y revisiones', lastCol: 'L',
    keyCol: null, boardCodeCol: 'A', writeRoles: ['admin', 'engineering'], exampleRows: [5],
    columns: [
      BOARD('A'),
      cap('B', 'docType', 'Tipo de documento', 'list', { list: 'tipo_doc', required: R }),
      cap('C', 'drawingNo', 'N° de plano', 'code', { required: R }),
      cap('D', 'revision', 'Revisión', 'text', { required: R }),
      cap('E', 'revisionDate', 'Fecha de revisión', 'date', { required: R }),
      cap('F', 'preparedBy', 'Elaboró', 'text'),
      cap('G', 'approvedBy', 'Aprobó', 'text'),
      cap('H', 'approvalStatus', 'Estado de aprobación', 'list', { list: 'est_apr', required: R }),
      cap('I', 'changeDescription', 'Descripción del cambio', 'longText'),
      cap('J', 'isCurrent', 'Es la revisión vigente', 'list', { list: 'sino', required: R }),
      auto('K', 'alert', 'Alerta', 'text'),
      NOTES('L'),
    ],
  },
  'MAT-01': {
    name: 'MAT-01', id: 'bom', title: 'Lista de materiales (BOM)', lastCol: 'N',
    keyCol: null, boardCodeCol: 'A', writeRoles: ['admin', 'engineering', 'purchasing'], exampleRows: [5],
    columns: [
      BOARD('A'),
      cap('B', 'item', 'Ítem', 'int', { min: 1 }),
      cap('C', 'partNo', 'N° de parte', 'code', { required: R }),
      cap('D', 'description', 'Descripción', 'text', { required: R }),
      cap('E', 'brand', 'Marca', 'text'),
      cap('F', 'budgetQty', 'Cantidad presupuestada', 'number', { required: R, min: 0, minExclusive: R }),
      cap('G', 'unit', 'Unidad', 'list', { list: 'unidad', required: R }),
      cap('H', 'unitCost', 'Costo unitario', 'money', { required: R, min: 0, cost: R }),
      auto('I', 'budgetCost', 'Costo presupuestado', 'money', { cost: R }),
      auto('J', 'consumedQty', 'Cantidad consumida (MAT-02)', 'number'),
      auto('K', 'pendingQty', 'Pendiente por entregar', 'number'),
      auto('L', 'actualCost', 'Costo real', 'money', { cost: R }),
      auto('M', 'costDeviation', 'Desviación (real − presup.)', 'money', { cost: R }),
      NOTES('N'),
    ],
  },
  'MAT-02': {
    name: 'MAT-02', id: 'stockMoves', title: 'Vales de entrada, entrega y devolución', lastCol: 'L',
    keyCol: null, boardCodeCol: 'D', writeRoles: ['admin', 'warehouse'], exampleRows: [5],
    columns: [
      cap('A', 'date', 'Fecha', 'date', { required: R }),
      cap('B', 'voucherNo', 'N° de vale', 'text', { required: R }),
      cap('C', 'moveType', 'Tipo de movimiento', 'list', { list: 'tipo_mov', required: R }),
      cap('D', 'boardCode', 'Código de tablero', 'ref', { ref: 'OT-01', requiredWhen: { col: 'C', notEquals: 'Entrada' } }),
      cap('E', 'partNo', 'N° de parte', 'code', { required: R }),
      cap('F', 'description', 'Descripción', 'text'),
      cap('G', 'qty', 'Cantidad', 'number', { required: R, min: 0, minExclusive: R }),
      cap('H', 'unit', 'Unidad', 'list', { list: 'unidad', required: R }),
      cap('I', 'lot', 'Lote / serie', 'text'),
      cap('J', 'deliveredBy', 'Entregado por', 'text'),
      cap('K', 'receivedBy', 'Recibido por', 'text'),
      NOTES('L'),
    ],
  },
  'INV-01': {
    name: 'INV-01', id: 'inventory', title: 'Inventario de materiales', lastCol: 'M',
    keyCol: 'A', boardCodeCol: null, writeRoles: ['admin', 'purchasing', 'warehouse'], exampleRows: [5],
    columns: [
      cap('A', 'partNo', 'N° de parte', 'code', { required: R, unique: R, immutable: R }),
      cap('B', 'description', 'Descripción', 'text', { required: R }),
      cap('C', 'brand', 'Marca', 'text'),
      cap('D', 'location', 'Ubicación en bodega', 'text'),
      cap('E', 'openingStock', 'Existencia inicial', 'number', { required: R, min: 0 }),
      cap('F', 'minStock', 'Stock mínimo', 'number', { required: R, min: 0 }),
      auto('G', 'receipts', 'Entradas', 'number'),
      auto('H', 'issues', 'Entregas', 'number'),
      auto('I', 'returns', 'Devoluciones', 'number'),
      auto('J', 'onHand', 'Existencia actual', 'number'),
      auto('K', 'reserved', 'Reservado (pendiente en BOM)', 'number'),
      auto('L', 'available', 'Disponible', 'number'),
      auto('M', 'stockStatus', 'Estado', 'text'),
    ],
  },
  'PRD-01': {
    name: 'PRD-01', id: 'production', title: 'Hoja diaria de producción y horas', lastCol: 'K',
    keyCol: null, boardCodeCol: 'B', writeRoles: ['admin', 'supervisor'], exampleRows: [5],
    columns: [
      cap('A', 'date', 'Fecha', 'date', { required: R }),
      BOARD('B'),
      auto('C', 'boardType', 'Tipo de tablero (auto)', 'text'),
      cap('D', 'stage', 'Etapa', 'list', { list: 'etapa', required: R }),
      cap('E', 'activity', 'Actividad realizada', 'text', { required: R }),
      cap('F', 'technician', 'Técnico', 'text', { required: R }),
      cap('G', 'hours', 'Horas trabajadas', 'number', { required: R, min: 0, minExclusive: R, max: 24 }),
      cap('H', 'isRework', '¿Es retrabajo?', 'list', { list: 'sino', required: R }),
      cap('I', 'reportedProgress', 'Avance reportado %', 'percent'),
      cap('J', 'supervisor', 'Supervisor', 'text'),
      NOTES('K'),
    ],
  },
  'CAL-01': {
    name: 'CAL-01', id: 'inspections', title: 'Inspección durante el ensamble', lastCol: 'I',
    keyCol: null, boardCodeCol: 'B', writeRoles: ['admin', 'quality'], exampleRows: [5],
    columns: [
      cap('A', 'date', 'Fecha', 'date', { required: R }),
      BOARD('B'),
      cap('C', 'checkpoint', 'Punto de control', 'list', { list: 'fase', required: R }),
      cap('D', 'aspect', 'Aspecto inspeccionado', 'list', { list: 'punto', required: R }),
      cap('E', 'result', 'Resultado', 'list', { list: 'res_insp', required: R }),
      cap('F', 'inspector', 'Inspector', 'text', { required: R }),
      cap('G', 'ncrNo', 'N° de NCR asociado', 'code', { requiredWhen: { col: 'E', equals: 'No cumple' } }),
      auto('H', 'ncrCheck', 'Control de NCR', 'text'),
      NOTES('I'),
    ],
  },
  'CAL-02': {
    name: 'CAL-02', id: 'torque', title: 'Registro de torque y conexiones críticas', lastCol: 'M',
    keyCol: null, boardCodeCol: 'B', writeRoles: ['admin', 'quality', 'technician'], exampleRows: [5],
    columns: [
      cap('A', 'date', 'Fecha', 'date', { required: R }),
      BOARD('B'),
      cap('C', 'component', 'Componente / terminal', 'text', { required: R }),
      cap('D', 'location', 'Ubicación', 'text'),
      cap('E', 'specTorque', 'Torque especificado', 'number', { required: R, min: 0, minExclusive: R }),
      cap('F', 'unit', 'Unidad', 'list', { list: 'u_torque', required: R }),
      cap('G', 'appliedTorque', 'Torque aplicado', 'number', { required: R, min: 0 }),
      cap('H', 'wrenchId', 'ID del torquímetro', 'ref', { ref: 'INS-01' }),
      auto('I', 'instrumentStatus', 'Estado del instrumento (auto)', 'text'),
      cap('J', 'technician', 'Técnico', 'text', { required: R }),
      cap('K', 'verifiedBy', 'Verificó (Calidad)', 'text'),
      auto('L', 'result', 'Resultado (auto)', 'text'),
      NOTES('M'),
    ],
  },
  'FAT-01': {
    name: 'FAT-01', id: 'fatTests', title: 'Protocolo de pruebas finales (FAT)', lastCol: 'M',
    keyCol: null, boardCodeCol: 'A', writeRoles: ['admin', 'engineering', 'quality'], exampleRows: [5],
    columns: [
      BOARD('A'),
      cap('B', 'test', 'Prueba', 'list', { list: 'prueba', required: R }),
      cap('C', 'procedureNo', 'N° de procedimiento aprobado', 'text'),
      cap('D', 'measuredValue', 'Valor medido', 'text'),
      cap('E', 'unit', 'Unidad', 'text'),
      cap('F', 'acceptance', 'Criterio de aceptación', 'text'),
      cap('G', 'instrumentId', 'ID del instrumento', 'ref', { ref: 'INS-01' }),
      auto('H', 'instrumentStatus', 'Estado del instrumento (auto)', 'text'),
      auto('I', 'instrumentValidity', 'Validez del instrumento (auto)', 'text'),
      cap('J', 'result', 'Resultado', 'list', { list: 'res_fat', required: R }),
      cap('K', 'testDate', 'Fecha de prueba', 'date', { required: R }),
      cap('L', 'responsible', 'Responsable (nombre y firma)', 'text', { required: R }),
      NOTES('M'),
    ],
  },
  'SEG-01': {
    name: 'SEG-01', id: 'safety', title: 'Inspección de seguridad y EPP', lastCol: 'O',
    keyCol: null, boardCodeCol: null, writeRoles: ['admin', 'supervisor'], exampleRows: [5],
    columns: [
      cap('A', 'date', 'Fecha', 'date', { required: R }),
      cap('B', 'area', 'Área / tablero', 'text', { required: R }),
      cap('C', 'recordType', 'Tipo de registro', 'list', { list: 'tipo_hall', required: R }),
      cap('D', 'inspector', 'Inspector', 'text', { required: R }),
      cap('E', 'ppe', 'EPP completo', 'list', { list: 'sinona' }),
      cap('F', 'housekeeping', 'Orden y limpieza', 'list', { list: 'sinona' }),
      cap('G', 'loto', 'LOTO aplicado', 'list', { list: 'sinona' }),
      cap('H', 'riskAssessment', 'Evaluación de riesgos', 'list', { list: 'sinona' }),
      cap('I', 'energizedTestAuth', 'Prueba energizada autorizada', 'list', { list: 'sinona' }),
      cap('J', 'finding', 'Descripción del hallazgo', 'longText'),
      cap('K', 'correctiveAction', 'Acción correctiva', 'longText'),
      cap('L', 'responsible', 'Responsable', 'text'),
      cap('M', 'dueDate', 'Fecha límite', 'date'),
      cap('N', 'status', 'Estado', 'list', { list: 'est_seg', required: R }),
      auto('O', 'overdue', 'Vencida (auto)', 'text'),
    ],
  },
  'INS-01': {
    name: 'INS-01', id: 'instruments', title: 'Inventario y estado de instrumentos', lastCol: 'N',
    keyCol: 'A', boardCodeCol: null, writeRoles: ['admin', 'equipmentManager'], exampleRows: [5, 6],
    columns: [
      cap('A', 'instrumentId', 'ID del instrumento', 'code', { required: R, unique: R, immutable: R }),
      cap('B', 'name', 'Instrumento', 'text', { required: R }),
      cap('C', 'brand', 'Marca', 'text'),
      cap('D', 'model', 'Modelo', 'text'),
      cap('E', 'serialNo', 'N° de serie', 'text'),
      cap('F', 'assignedTo', 'Asignado a', 'text'),
      cap('G', 'lastCalibration', 'Fecha de última calibración / verificación', 'date'),
      cap('H', 'frequencyMonths', 'Frecuencia (meses)', 'int', { min: 0, minExclusive: R, requiredWhen: { col: 'G', notBlank: R } }),
      cap('I', 'certificateNo', 'N° de certificado', 'text'),
      cap('J', 'condition', 'Condición física', 'list', { list: 'cond_inst', required: R }),
      auto('K', 'nextCalibration', 'Próxima calibración (auto)', 'date'),
      auto('L', 'daysLeft', 'Días restantes (auto)', 'int'),
      auto('M', 'status', 'Estado (auto)', 'text'),
      NOTES('N'),
    ],
  },
  'NCR-01': {
    name: 'NCR-01', id: 'ncr', title: 'Fallas, retrabajos y acciones correctivas', lastCol: 'O',
    keyCol: 'A', boardCodeCol: 'C', writeRoles: ['admin', 'quality'], exampleRows: [5],
    columns: [
      cap('A', 'ncrNo', 'N° de NCR', 'code', { required: R, unique: R, immutable: R }),
      cap('B', 'detectedDate', 'Fecha de detección', 'date', { required: R }),
      BOARD('C'),
      cap('D', 'stage', 'Etapa donde se detectó', 'list', { list: 'etapa', required: R }),
      cap('E', 'description', 'Descripción de la no conformidad', 'longText', { required: R }),
      cap('F', 'rootCause', 'Causa raíz', 'longText'),
      cap('G', 'type', 'Tipo', 'list', { list: 'tipo_ncr', required: R }),
      cap('H', 'reworkHours', 'Horas de retrabajo', 'number', { min: 0 }),
      cap('I', 'extraCost', 'Costo adicional (materiales/otros)', 'money', { min: 0, cost: R }),
      cap('J', 'correctiveAction', 'Acción correctiva', 'longText'),
      cap('K', 'responsible', 'Responsable', 'text'),
      cap('L', 'commitDate', 'Fecha compromiso', 'date'),
      cap('M', 'closeDate', 'Fecha de cierre', 'date', { gteCol: 'B' }),
      cap('N', 'status', 'Estado', 'list', { list: 'est_ncr', required: R }),
      auto('O', 'daysOpen', 'Días abierta (auto)', 'int'),
    ],
  },
  'DES-01': {
    name: 'DES-01', id: 'releases', title: 'Acta de liberación y entrega', lastCol: 'P',
    keyCol: 'A', boardCodeCol: 'A', writeRoles: ['admin', 'releaseAuthority'], exampleRows: [5],
    columns: [
      BOARD('A', 'Código de tablero', { unique: R, immutable: R }),
      auto('B', 'client', 'Cliente (auto)', 'text'),
      cap('C', 'finalInspection', 'Inspección final aprobada', 'list', { list: 'sino', required: R }),
      auto('D', 'fatComplete', 'FAT aprobado completo (auto)', 'text'),
      cap('E', 'labeled', 'Equipo identificado / rotulado', 'list', { list: 'sino', required: R }),
      auto('F', 'ncrClosed', 'Pendientes (NCR) cerrados (auto)', 'text'),
      cap('G', 'asBuilt', 'Planos finales conforme a obra', 'list', { list: 'sinona', required: R }),
      cap('H', 'fatProtocols', 'Protocolos FAT', 'list', { list: 'sinona', required: R }),
      cap('I', 'manuals', 'Manuales', 'list', { list: 'sinona', required: R }),
      cap('J', 'protectionSettings', 'Ajustes de protecciones', 'list', { list: 'sinona', required: R }),
      cap('K', 'warranty', 'Garantía', 'list', { list: 'sinona', required: R }),
      auto('L', 'result', 'Resultado (auto)', 'text'),
      cap('M', 'releaseDate', 'Fecha de liberación', 'date'),
      cap('N', 'approvedBy', 'Aprobó (Calidad / autorizado)', 'text'),
      cap('O', 'receivedBy', 'Recibido por (cliente)', 'text'),
      NOTES('P'),
    ],
  },
  'COS-01': {
    name: 'COS-01', id: 'costs', title: 'Costos, desviaciones y rentabilidad', lastCol: 'P',
    keyCol: null, boardCodeCol: 'A', rowLinkedToWorkOrders: true, costSheet: true,
    writeRoles: ['admin', 'management'], exampleRows: [5],
    columns: [
      auto('A', 'boardCode', 'Código de tablero (auto)', 'text'),
      auto('B', 'client', 'Cliente (auto)', 'text'),
      cap('C', 'salePrice', 'Precio de venta', 'money', { min: 0, cost: R }),
      auto('D', 'budgetMaterials', 'Materiales presupuestados', 'money', { cost: R }),
      auto('E', 'actualMaterials', 'Materiales reales', 'money', { cost: R }),
      auto('F', 'materialsDeviation', 'Desviación materiales', 'money', { cost: R }),
      auto('G', 'budgetHours', 'Horas presupuestadas', 'number', { cost: R }),
      auto('H', 'actualHours', 'Horas reales', 'number', { cost: R }),
      auto('I', 'hoursDeviation', 'Desviación horas', 'number', { cost: R }),
      auto('J', 'laborCost', 'Costo mano de obra', 'money', { cost: R }),
      cap('K', 'urgentPurchases', 'Compras urgentes', 'money', { min: 0, cost: R }),
      cap('L', 'materialWaste', 'Desperdicio de material', 'money', { min: 0, cost: R }),
      auto('M', 'reworkLaborCost', 'Costo MO en retrabajo (ya incluido en MO)', 'money', { cost: R }),
      auto('N', 'totalCost', 'Costo total de fabricación', 'money', { cost: R }),
      auto('O', 'profit', 'Utilidad', 'money', { cost: R }),
      auto('P', 'margin', 'Margen %', 'percent', { cost: R }),
    ],
  },
};

/** Parámetros editables (celdas amarillas). */
export const PARAMETERS = [
  { id: 'torqueTolerance', sheet: 'CAL-02', cell: 'D3', type: 'percent', label: 'Tolerancia de torque aceptada (± %)', cost: false },
  { id: 'warningDays', sheet: 'INS-01', cell: 'D3', type: 'int', label: 'Días de aviso antes del vencimiento', cost: false },
  { id: 'laborCostPerHour', sheet: 'COS-01', cell: 'D3', type: 'money', label: 'Costo por hora de mano de obra', cost: true },
] as const satisfies ReadonlyArray<{ id: string; sheet: SheetName; cell: string; type: FieldType; label: string; cost: boolean }>;
export type ParameterId = (typeof PARAMETERS)[number]['id'];

/** Indicadores del Dashboard (filas 6–17). */
export const KPI_ROWS = [
  { row: 6, id: 'onTimeDelivery', cost: false },
  { row: 7, id: 'firstPassYield', cost: false },
  { row: 8, id: 'productionEfficiency', cost: false },
  { row: 9, id: 'materialsDeviation', cost: true },
  { row: 10, id: 'reworkRatio', cost: false },
  { row: 11, id: 'documentTraceability', cost: false },
  { row: 12, id: 'releasesWithPendingTests', cost: false },
  { row: 13, id: 'safetyIncidents', cost: false },
  { row: 14, id: 'openUnsafeConditions', cost: false },
  { row: 15, id: 'unfitInstruments', cost: false },
  { row: 16, id: 'openNcr', cost: false },
  { row: 17, id: 'profitMargin', cost: true },
] as const;
export type KpiId = (typeof KPI_ROWS)[number]['id'];

// ───────── utilidades de consulta ─────────

export function sheetDef(name: SheetName): SheetDef {
  return SCHEMA[name];
}

export function isFormatSheet(name: string): name is SheetName {
  return (FORMAT_SHEETS as readonly string[]).includes(name);
}

export function captureColumns(sheet: SheetName): ColumnDef[] {
  return SCHEMA[sheet].columns.filter((c) => c.kind === 'capture');
}

export function autoColumns(sheet: SheetName): ColumnDef[] {
  return SCHEMA[sheet].columns.filter((c) => c.kind === 'auto');
}

export function column(sheet: SheetName, col: string): ColumnDef {
  const def = SCHEMA[sheet].columns.find((c) => c.col === col);
  if (!def) throw new Error(`Columna ${col} no existe en ${sheet}`);
  return def;
}

export function costColumns(sheet: SheetName): ColumnDef[] {
  const s = SCHEMA[sheet];
  return s.costSheet ? [...s.columns] : s.columns.filter((c) => c.cost);
}

/** Rango de lectura completo de un formato: encabezado + 200 filas. */
export function sheetReadRange(sheet: SheetName): string {
  return `'${sheet}'!A${HEADER_ROW}:${SCHEMA[sheet].lastCol}${LAST_DATA_ROW}`;
}
